import { Box, Card, ClickAwayListener, IconButton, Paper, Popper, Typography } from "@mui/material";
import { ErrorIcon, CalendarIcon, withTooltip, RightArrow, LeftArrow } from "../../../../../../common/icons/Icons";
import { dateUtils } from "../../../../../../utils/DateUtils";
import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarPicker, PickersDay, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const locale = navigator.language;

const calendarDayStyle = (theme) => ({
    '&.selected': {
        backgroundColor: `${theme.palette.primary.light}80`,
        'button': {
            backgroundColor: `transparent`,
            '&:hover': {
                backgroundColor: `rgba(0, 0, 0, 0.04)`,
            }
        }
    },
    '&.selectionEnd': {
        background: `linear-gradient(90deg, ${theme.palette.primary.light}80 50%, rgba(255,255,255,1) 50%, rgba(255,255,255,1) 100%);`,
        '& button.Mui-selected': {
            backgroundColor: 'primary.main',
        },
        '& button': {
            backgroundColor: 'primary.light',
        },
    },
    '&.selectionStart': {
        background: `linear-gradient(90deg, rgba(255,255,255,1) 50%, ${theme.palette.primary.light}80 50%, ${theme.palette.primary.light}80 100%);`,
        '& button.Mui-selected': {
            backgroundColor: 'primary.main',
        },
        '& button': {
            backgroundColor: 'primary.light',
        },
    },
})

const DayCard = (props) => {
    const { Icon, value, onChange, editing, valid, errorMessage, calendarProps = {}, cardStyle, minDate, maxDate, shouldDisableDate } = props;

    const extendedDate = dateUtils.getExtendedDate(value, locale);

    //calculating arrows disabled/enabled
    const previousDate = (d => new Date(d.setDate(d.getDate() - 1)))(new Date(value.getTime()));
    const nextDate = (d => new Date(d.setDate(d.getDate() + 1)))(new Date(value.getTime()));

    const minusDisabled = (shouldDisableDate && shouldDisableDate(previousDate)) || (minDate && previousDate < minDate);
    const plusDisabled = (shouldDisableDate && shouldDisableDate(nextDate)) || (maxDate && nextDate > maxDate);

    const plusDay = () => {
        !plusDisabled && onChange(nextDate);
    }

    const minusDay = () => {
        !minusDisabled && onChange(previousDate);
    }

    const onCalendarSelect = (selectedDate) => {
        selectedDate.setHours(value.getHours());
        selectedDate.setMinutes(value.getMinutes());

        onChange(selectedDate);
    }

    return <Card sx={{ minWidth: '100px', ...cardStyle }}>
        <Icon style={{ margin: '10px auto' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', width: '100%', height: '40px' }}>
            <Box sx={{ width: '34px', display: 'flex', justifyContent: 'center' }}>
                {!valid && <ErrorIcon
                    data-testid='errorIcon'
                    title={errorMessage}
                />}
            </Box>
            <Typography sx={{ width: '35px' }}>
                {`${extendedDate.dayOfWeek}`}
            </Typography>
            <Box sx={{ width: '34px', display: 'flex', justifyContent: 'center' }}>
                {editing && <Calendar onChange={onCalendarSelect} date={value} calendarProps={{ ...calendarProps, minDate, maxDate, shouldDisableDate }} />}
            </Box>
        </Box>

        <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', color: !valid ? 'error.main' : 'text' }}>
            <LeftArrow data-testid='previousDayButton' size='small' disabled={minusDisabled} hidden={!editing} onClick={minusDay} title='Previous day' />
            {`${extendedDate.dayOfMonth} ${extendedDate.monthNameShort} ${extendedDate.year}`}
            <RightArrow data-testid='nextDayButton' size='small' disabled={plusDisabled} hidden={!editing} onClick={plusDay} title='Next day' />
        </Typography>
        <Typography>{extendedDate.time}</Typography>
    </Card>
}

const Calendar = (props) => {
    const { date, onChange, calendarProps, disabled } = props;
    const { initialHighlight, ...otherCalendarProps } = calendarProps;

    const [highlightStart, setHighlightStart] = useState(dateUtils.atMidnight(initialHighlight ? initialHighlight.start : date));
    const [highlightEnd, setHighlightEnd] = useState(dateUtils.atMidnight(initialHighlight ? initialHighlight.end : date));

    const [anchorEl, setAnchorEl] = useState(null);

    const iconJustClicked = useRef(false);

    const handleIconClick = (evt) => {
        if (disabled) {
            return;
        }
        if (anchorEl) {
            setAnchorEl(null);
        }
        else {
            setAnchorEl(evt.currentTarget.parentElement.parentElement);
            iconJustClicked.current = true;
        }
    }

    const onSelect = (selectedDate, selectionState) => {
        if (selectionState === 'finish') {
            setAnchorEl(null);
        }
        onChange(selectedDate);
    }

    const resetHighlight = useCallback(() => {
        setHighlightStart(dateUtils.atMidnight(initialHighlight ? initialHighlight.start : date));
        setHighlightEnd(dateUtils.atMidnight(initialHighlight ? initialHighlight.end : date));
    }, [initialHighlight, date]);

    useEffect(() => {
        resetHighlight();
    }, [resetHighlight]);

    const Day = (props) => <PickersDay {...props} />;

    return <>
        <CalendarIcon onClick={handleIconClick} data-testid='calendarIcon' />
        <Popper open={!!anchorEl} anchorEl={anchorEl} sx={{ zIndex: 1300 }}>
            <Paper>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                        <CalendarPicker
                            onChange={onSelect}
                            date={date}
                            components={{
                                LeftArrowButton: withTooltip(IconButton),
                                RightArrowButton: withTooltip(IconButton),
                            }}
                            componentsProps={{
                                leftArrowButton: {
                                    disabledTitle: 'Previous month is not available'
                                },
                                rightArrowButton: {
                                    disabledTitle: 'Next month is not available'
                                }
                            }}
                            renderDay={(day, selectedDays, pickersDayProps) => {
                                if (highlightStart.getTime() >= highlightEnd.getTime() || pickersDayProps.outsideCurrentMonth) {
                                    //no need to customize
                                    return <Day {...pickersDayProps} />;
                                }
                                else if (day.getTime() === highlightStart.getTime()) {
                                    return <Box key={pickersDayProps.key} sx={calendarDayStyle} className='selectionStart'><Day {...pickersDayProps} /></Box>;
                                }
                                else if (day.getTime() === highlightEnd.getTime()) {
                                    return <Box key={pickersDayProps.key} sx={calendarDayStyle} className='selectionEnd'><Day {...pickersDayProps} /></Box>;
                                }
                                else if (highlightStart < day && day < highlightEnd) {
                                    return <Box key={pickersDayProps.key} sx={calendarDayStyle} className='selected'><Day {...pickersDayProps} /></Box>;
                                }
                                else return <Day {...pickersDayProps} />;
                            }}
                            {...otherCalendarProps}
                        />
                    </ClickAwayListener>
                </LocalizationProvider>
            </Paper>
        </Popper>
    </>
}

export default DayCard;